import type { QueryClient } from '@tanstack/react-query';

import { defaultClient } from '@mint-sdk/api-core';
import { z } from 'zod';

import {
  ProfileTypeSchema,
  UserProfileCreateSchema,
  UserProfileSchema,
  UserProfileUpdateSchema,
} from '../shared/schemas/UserProfileSchema.js';

export type UserProfileType = z.infer<typeof UserProfileSchema>;
export type ProfileType = z.infer<typeof ProfileTypeSchema>;
export type UserProfileCreateType = z.infer<typeof UserProfileCreateSchema>;
export type UserProfileUpdateType = z.infer<typeof UserProfileUpdateSchema>;

export const listUserProfiles = defaultClient.query({
  url: 'ai-assistant/user-profiles' as const,
  method: 'GET',
  responseSchema: z.array(UserProfileSchema),
  querySchema: z.object({ type: ProfileTypeSchema.optional() }),
  snakeCaseQueryParams: true,
});

export const getActiveUserProfile = defaultClient.query({
  url: 'ai-assistant/user-profiles/active' as const,
  method: 'GET',
  responseSchema: UserProfileSchema.nullable(),
});

const invalidateUserProfiles = (queryClient: QueryClient) => {
  listUserProfiles.invalidateAll(queryClient, {});
  getActiveUserProfile.invalidateAll(queryClient, {});
};

export const createUserProfile = defaultClient.mutation({
  url: 'ai-assistant/user-profiles' as const,
  method: 'POST',
  requestSchema: UserProfileCreateSchema,
  responseSchema: UserProfileSchema,
  onSuccess: (queryClient) => invalidateUserProfiles(queryClient),
  processResponse(data) {
    return data;
  },
});

export const updateUserProfile = defaultClient.mutation({
  url: 'ai-assistant/user-profiles/$id' as const,
  method: 'PATCH',
  requestSchema: UserProfileUpdateSchema,
  responseSchema: UserProfileSchema,
  onSuccess: (queryClient) => invalidateUserProfiles(queryClient),
  processResponse(data) {
    return data;
  },
});

export const deleteUserProfile = defaultClient.mutation({
  url: 'ai-assistant/user-profiles/$id' as const,
  method: 'DELETE',
  responseSchema: z.unknown(),
  onSuccess: (queryClient) => invalidateUserProfiles(queryClient),
  processResponse(data) {
    return data;
  },
});

export const setActiveUserProfile = defaultClient.mutation({
  url: 'ai-assistant/user-profiles/$id' as const,
  method: 'PATCH',
  requestSchema: z.object({ isActive: z.literal(true) }),
  responseSchema: UserProfileSchema,
  onSuccess: (queryClient) => invalidateUserProfiles(queryClient),
  processResponse(data) {
    return data;
  },
});
